import React, { createContext, useContext, useRef } from 'react';
import { createEventStore } from './EventsStore';
import { createAppStore } from './AppStore';
import { createPlayerStore } from './PlayerStore';
import { createCheckinStore } from './CheckinStore';
import { createExamSessionStore } from './ExamSessionStore';
import { createVideoFragmentStore } from './VideoFragmentStore';
import { createLayoutStore } from './LayoutStore';

const StoreContext = createContext(null);

export const StoreProvider = ({ children, initialState = {} }) => {
  // create store instances with placeholder deps
  const appStore = useRef(createAppStore(initialState.app)).current;
  const layoutStore = useRef(createLayoutStore(initialState.layout)).current;
  const eventStore = useRef(createEventStore(initialState.events)).current;
  const playerStore = useRef(createPlayerStore(initialState.player)).current;
  const checkinStore = useRef(createCheckinStore(initialState.checkin)).current;
  const examSessionStore = useRef(
    createExamSessionStore(initialState.examSession)
  ).current;
  const videoFragmentStore = useRef(
    createVideoFragmentStore(initialState.videoFragment)
  ).current;

  // inject all the dependencies
  appStore
    .getState()
    .injectDependentStores({ playerStore, examSessionStore, eventStore });
  playerStore.getState().injectDependentStores({ examSessionStore, appStore });
  layoutStore
    .getState()
    .injectDependentStores({ checkinStore, appStore, examSessionStore });
  checkinStore.getState().injectDependentStores({ layoutStore, eventStore });
  examSessionStore.getState().injectDependentStores({ playerStore, appStore });
  eventStore.getState().injectDependentStores({ appStore, examSessionStore });
  videoFragmentStore
    .getState()
    .injectDependentStores({ appStore, examSessionStore });

  const stores = {
    appStore,
    layoutStore,
    eventStore,
    playerStore,
    checkinStore,
    examSessionStore,
    videoFragmentStore,
  };

  return (
    <StoreContext.Provider value={stores}>{children}</StoreContext.Provider>
  );
};

export const useStores = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStores must be used inside StoreProvider');
  return ctx;
};

const withStoreSelector = (storeKey) => (selector, equals) => {
  const ctx = useStores();
  return ctx[storeKey](selector, equals);
};

const withRawStore = (storeKey) => () => {
  const ctx = useStores();
  return ctx[storeKey];
};

export const getStoreRaw = (storeKey) => {
  const context = useContext(StoreContext);
  if (!context)
    throw new Error('useScopedStoreRef must be used inside StoreProvider');
  return context[storeKey]; // this is the Zustand store instance
};

export const getStore = (storeKey) => {
  const context = useContext(StoreContext);
  if (!context)
    throw new Error('useScopedStoreRef must be used inside StoreProvider');
  return context[storeKey].getState(); // this is the Zustand store instance
};

// Hook-style selectors
export const useAppStore = withStoreSelector('appStore');
export const useLayoutStore = withStoreSelector('layoutStore');
export const useEventStore = withStoreSelector('eventStore');
export const usePlayerStore = withStoreSelector('playerStore');
export const useCheckinStore = withStoreSelector('checkinStore');
export const useExamSessionStore = withStoreSelector('examSessionStore');
export const useVideoFragmentStore = withStoreSelector('videoFragmentStore');

// Raw store access
export const getAppStore = withRawStore('appStore');
export const getLayoutStore = withRawStore('layoutStore');
export const getEventStore = withRawStore('eventStore');
export const getPlayerStore = withRawStore('playerStore');
export const getCheckinStore = withRawStore('checkinStore');
export const getExamSessionStore = withRawStore('examSessionStore');
export const getVideoFragmentStore = withRawStore('videoFragmentStore');
